'use client';

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import {
  Drawer,
  List,
  ListItemButton,
  Typography,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  ChevronRight,
  ExpandMore,
  Bolt as LightningIcon,
  UnfoldMore as UnfoldMoreIcon,
} from '@mui/icons-material';
import { Logo } from './Logo';
import { TenantMenu } from './TenantMenu';
import { navigationGroups, footerNavigationItems } from '@/lib/navigation';
import type { NavigationItem } from '@/types/navigation';
import { useNavigationVisibility } from '@/contexts/NavigationVisibilityContext';
import { useAuth } from '@/hooks/useAuth';
import { useNav } from '@/contexts/NavContext';
import { useTenant } from '@/contexts/TenantContext';

interface SideNavProps {
  open: boolean;
  onClose: () => void;
  variant?: 'permanent' | 'temporary' | 'persistent';
}

interface ItemPopout {
  item: NavigationItem;
  rect: DOMRect;
}

export function SideNav({ open, onClose, variant = 'temporary' }: SideNavProps) {
  const pathname = usePathname();
  const { visibility } = useNavigationVisibility();
  const { roles } = useAuth();
  const { isExpanded } = useNav();
  const { selectedTenant } = useTenant();
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const [tenantMenuAnchor, setTenantMenuAnchor] = useState<null | HTMLElement>(null);
  const [itemPopout, setItemPopout] = useState<ItemPopout | null>(null);
  const [popoutExpandedItems, setPopoutExpandedItems] = useState<Record<string, boolean>>({});
  const sidebarPaperRef = useRef<HTMLDivElement>(null);
  const popoutCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPopoutClose = useCallback(() => {
    if (popoutCloseRef.current) {
      clearTimeout(popoutCloseRef.current);
      popoutCloseRef.current = null;
    }
  }, []);

  const schedulePopoutClose = useCallback(() => {
    if (popoutCloseRef.current) clearTimeout(popoutCloseRef.current);
    popoutCloseRef.current = setTimeout(() => setItemPopout(null), 200);
  }, []);

  const openPopout = useCallback(
    (item: NavigationItem, el: HTMLElement) => {
      cancelPopoutClose();
      setItemPopout((prev) => {
        if (prev?.item.id === item.id) {
          return prev;
        }
        return { item, rect: el.getBoundingClientRect() };
      });
    },
    [cancelPopoutClose],
  );

  useEffect(() => {
    return () => {
      if (popoutCloseRef.current) clearTimeout(popoutCloseRef.current);
    };
  }, []);

  useEffect(() => {
    if (isExpanded) {
      cancelPopoutClose();
      setItemPopout(null);
    }
  }, [isExpanded, cancelPopoutClose]);

  useEffect(() => {
    cancelPopoutClose();
    setItemPopout(null);
  }, [pathname, cancelPopoutClose]);

  useEffect(() => {
    const sidenavContent = sidebarPaperRef.current?.querySelector('.sidenav-content');
    if (!sidenavContent || isExpanded) return;

    const handleScroll = () => {
      if (itemPopout) {
        setItemPopout(null);
      }
    };

    sidenavContent.addEventListener('scroll', handleScroll);
    return () => sidenavContent.removeEventListener('scroll', handleScroll);
  }, [isExpanded, itemPopout]);

  useEffect(() => {
    const item = itemPopout?.item;
    if (!item) return;
    const autoExpand: Record<string, boolean> = {};
    const markActive = (items: NavigationItem[]): boolean =>
      items.some((child) => {
        const childActive = child.children ? markActive(child.children) : false;
        if (childActive) autoExpand[child.id] = true;
        return Boolean(child.path && pathname === child.path) || childActive;
      });
    if (item.children) markActive(item.children);
    setPopoutExpandedItems(autoExpand);
  }, [itemPopout?.item, pathname]);

  const activePathMap = useMemo(() => {
    const pathMap = new Map<string, boolean>();

    const checkAndExpandParents = (items: NavigationItem[]): boolean => {
      return items.some((item) => {
        let isActiveInBranch = false;

        if (item.path && pathname === item.path) {
          pathMap.set(item.id, true);
          isActiveInBranch = true;
        }

        if (item.children && item.children.length > 0) {
          const hasActiveChild = checkAndExpandParents(item.children);
          if (hasActiveChild) {
            pathMap.set(item.id, true);
            isActiveInBranch = true;
          }
        }

        return isActiveInBranch;
      });
    };

    navigationGroups.forEach((group) => {
      checkAndExpandParents(group.items);
    });

    return pathMap;
  }, [pathname]);

  useEffect(() => {
    const autoExpandItems: Record<string, boolean> = {};
    activePathMap.forEach((shouldExpand, itemId) => {
      if (shouldExpand) autoExpandItems[itemId] = true;
    });
    setExpandedItems((prev) => ({ ...prev, ...autoExpandItems }));
  }, [activePathMap]);

  const toggleExpand = (id: string) => {
    setExpandedItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const isItemVisible = (item: NavigationItem): boolean => {
    if (item.hidden) return false;
    if (item.requiredRoles?.length) {
      if (!item.requiredRoles.some((role) => roles.includes(role))) return false;
    }
    if (item.id in visibility) {
      return visibility[item.id as keyof typeof visibility];
    }
    return true;
  };

  const isItemActive = (item: NavigationItem): boolean => {
    return item.path ? pathname === item.path : false;
  };

  const renderNavigationItem = (item: NavigationItem, level: number = 0) => {
    if (!isItemVisible(item)) return null;

    const Icon = item.icon;
    const isActive = isItemActive(item) || (!isExpanded && level === 0 && activePathMap.has(item.id));
    const isItemExpanded = expandedItems[item.id];
    const hasChildren = item.children?.some((c) => isItemVisible(c)) ?? false;
    const isClickable = Boolean(item.path);

    const handleChevronClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      toggleExpand(item.id);
    };

    const itemContent = (
      <div className="sidenav-item-content">
        {Icon && <Icon className="sidenav-item-icon" size={20} />}
        {isExpanded && <span className="sidenav-item-label">{item.label}</span>}
        {isExpanded && item.isSpecial && <LightningIcon className="sidenav-special-icon" />}
        {isExpanded && (item.isExpandable || hasChildren) && (
          <span className="sidenav-expand-icon" onClick={handleChevronClick}>
            {isItemExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
          </span>
        )}
      </div>
    );

    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();

      if (!isExpanded) {
        if (!isClickable && (hasChildren || item.isExpandable)) e.preventDefault();
        return;
      }

      if (!isClickable && (hasChildren || item.isExpandable)) {
        e.preventDefault();
        toggleExpand(item.id);
      }

      if (variant === 'temporary' && isClickable) onClose();
    };

    const hasPopout = variant === 'permanent' && !isExpanded && level === 0;
    const listItem = (
      <ListItemButton
        key={item.id}
        onClick={handleClick}
        component={isClickable ? Link : 'div'}
        href={isClickable && item.path ? item.path : undefined}
        className={`sidenav-item sidenav-item-level-${level} ${isActive ? 'active' : ''}`}
        onMouseEnter={
          hasPopout
            ? (e: React.MouseEvent<HTMLElement>) => openPopout(item, e.currentTarget)
            : undefined
        }
        onFocus={
          hasPopout
            ? (e: React.FocusEvent<HTMLElement>) => openPopout(item, e.currentTarget)
            : undefined
        }
        onBlur={hasPopout ? schedulePopoutClose : undefined}
        onMouseLeave={hasPopout ? schedulePopoutClose : undefined}
      >
        {itemContent}
      </ListItemButton>
    );

    const wrappedListItem = hasPopout ? (
      listItem
    ) : !isExpanded && level === 0 ? (
      <Tooltip key={item.id} title={item.label} placement="right">
        {listItem}
      </Tooltip>
    ) : (
      listItem
    );

    if (hasChildren && item.children) {
      return (
        <React.Fragment key={item.id}>
          {wrappedListItem}
          <Collapse in={isItemExpanded && isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((child) => renderNavigationItem(child, level + 1))}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }

    return wrappedListItem;
  };

  const renderPopoutTreeItem = (item: NavigationItem, level: number = 0): React.ReactNode => {
    if (!isItemVisible(item)) return null;
    const Icon = item.icon;
    const isActive = isItemActive(item);
    const isClickable = Boolean(item.path);
    const hasChildren = item.children?.some((c) => isItemVisible(c)) ?? false;
    const isNodeExpanded = popoutExpandedItems[item.id];
    const isParentInActivePath = !isActive && activePathMap.has(item.id);

    const toggleNode = (e: React.SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setPopoutExpandedItems((prev) => ({ ...prev, [item.id]: !prev[item.id] }));
    };

    const classNames = [
      'sidenav-popout-tree-item',
      `sidenav-popout-tree-level-${level}`,
      isActive ? 'active' : '',
      isParentInActivePath ? 'parent-in-active-path' : '',
      isNodeExpanded ? 'expanded' : '',
    ].filter(Boolean).join(' ');

    return (
      <React.Fragment key={item.id}>
        <ListItemButton
          component={isClickable ? Link : 'div'}
          href={isClickable && item.path ? item.path : undefined}
          aria-expanded={hasChildren ? isNodeExpanded : undefined}
          onClick={(e: React.MouseEvent) => {
            if (!isClickable && hasChildren) { toggleNode(e); return; }
            cancelPopoutClose();
            setItemPopout(null);
          }}
          className={classNames}
        >
          {Icon && level === 0 && <Icon className="sidenav-popout-tree-icon" size={16} />}
          <span className="sidenav-popout-tree-label">{item.label}</span>
          {hasChildren && (
            <span className="sidenav-popout-tree-chevron" onClick={toggleNode}>
              {isNodeExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
            </span>
          )}
        </ListItemButton>
        {hasChildren && item.children && (
          <Collapse in={isNodeExpanded} timeout="auto" unmountOnExit>
            <List disablePadding>
              {item.children.map((child) => renderPopoutTreeItem(child, level + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawerContent = (
    <div className="sidenav-container">
      <div className="sidenav-header">
        <Logo collapsed={!isExpanded} variant="dark" />
      </div>

      <div className="sidenav-content">
        {navigationGroups.map((group) => {
          const hasVisibleItems = group.items.some(isItemVisible);
          if (!hasVisibleItems) return null;
          return (
            <div key={group.id} className="sidenav-group">
              {group.title && isExpanded && (
                <Typography className="sidenav-group-title">{group.title}</Typography>
              )}
              <List component="nav" className="sidenav-list">
                {group.items.map((item) => renderNavigationItem(item))}
              </List>
            </div>
          );
        })}
      </div>

      <div className="sidenav-footer">
        <List component="nav" className="sidenav-footer-list">
          {footerNavigationItems.map((item) => {
            const Icon = item.icon;
            const displayLabel =
              item.id === 'demonstration-tenant' && selectedTenant
                ? selectedTenant
                : item.label;

            const handleFooterClick = () => {
              if (item.id === 'demonstration-tenant') {
                setTenantMenuAnchor(sidebarPaperRef.current);
              } else {
                toggleExpand(item.id);
              }
            };

            const footerItem = (
              <ListItemButton
                key={item.id}
                onClick={handleFooterClick}
                className="sidenav-footer-item"
                onMouseEnter={!isExpanded ? schedulePopoutClose : undefined}
              >
                <div className="sidenav-item-content">
                  {Icon && <Icon className="sidenav-item-icon" size={20} />}
                  {isExpanded && <span className="sidenav-item-label">{displayLabel}</span>}
                  {isExpanded && (
                    <span className="sidenav-expand-icon">
                      {item.id === 'demonstration-tenant' ? (
                        <UnfoldMoreIcon fontSize="small" />
                      ) : (
                        <ChevronRight fontSize="small" />
                      )}
                    </span>
                  )}
                </div>
              </ListItemButton>
            );

            return !isExpanded ? (
              <Tooltip key={item.id} title={displayLabel} placement="right">
                {footerItem}
              </Tooltip>
            ) : (
              footerItem
            );
          })}
        </List>
      </div>

      <TenantMenu
        anchorEl={tenantMenuAnchor}
        open={Boolean(tenantMenuAnchor)}
        onClose={() => setTenantMenuAnchor(null)}
      />
    </div>
  );

  if (variant === 'permanent') {
    const sidebarRight = itemPopout
      ? (sidebarPaperRef.current?.getBoundingClientRect().right ?? 55)
      : 0;
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;

    const countExpandedNodes = (items: NavigationItem[]): number => {
      let count = 0;
      items.forEach((item) => {
        if (isItemVisible(item)) {
          count += 1;
          if (item.children && popoutExpandedItems[item.id]) {
            count += countExpandedNodes(item.children);
          }
        }
      });
      return count;
    };

    const HeaderIcon = itemPopout?.item.icon;
    const hasChildren = itemPopout?.item.children?.some((c) => isItemVisible(c)) ?? false;
    const isPopoutHeaderExpanded = itemPopout?.item.id ? popoutExpandedItems[itemPopout.item.id] ?? true : true;

    const totalVisibleItems = (itemPopout?.item.children && isPopoutHeaderExpanded)
      ? countExpandedNodes(itemPopout.item.children)
      : 0;
    const estimatedHeight = Math.min(totalVisibleItems * 36 + 44, viewportHeight * 0.8);
    const popoutTop = itemPopout
      ? Math.max(8, Math.min(itemPopout.rect.top, viewportHeight - estimatedHeight - 8))
      : 0;
    const isPopoutHeaderActive = itemPopout?.item.path ? pathname === itemPopout.item.path : false;

    const togglePopoutHeader = () => {
      if (itemPopout?.item.id) {
        setPopoutExpandedItems((prev) => ({
          ...prev,
          [itemPopout.item.id]: !(prev[itemPopout.item.id] ?? true),
        }));
      }
    };

    return (
      <>
        <div
          ref={sidebarPaperRef}
          className="sidenav-paper"
          onMouseEnter={!isExpanded ? cancelPopoutClose : undefined}
          onMouseLeave={!isExpanded ? schedulePopoutClose : undefined}
        >
          {drawerContent}
        </div>

        {itemPopout && !isExpanded && typeof document !== 'undefined' &&
          createPortal(
            <div
              className="sidenav-item-popout"
              style={{ left: sidebarRight + 4, top: popoutTop }}
              onMouseEnter={cancelPopoutClose}
              onMouseLeave={schedulePopoutClose}
              onFocus={cancelPopoutClose}
              onBlur={schedulePopoutClose}
              role="navigation"
              aria-label={`${itemPopout.item.label} submenu`}
            >
              <div
                className={`sidenav-popout-header${hasChildren ? '' : ' sidenav-popout-header-leaf'}${isPopoutHeaderExpanded ? ' expanded' : ''}${isPopoutHeaderActive ? ' active' : ''}`}
                onClick={hasChildren ? togglePopoutHeader : undefined}
                style={{ cursor: hasChildren ? 'pointer' : 'default' }}
              >
                {HeaderIcon && <HeaderIcon className="sidenav-popout-header-icon" size={16} />}
                <span className="sidenav-popout-header-label">{itemPopout.item.label}</span>
                {hasChildren && (
                  <span className="sidenav-popout-header-chevron">
                    {isPopoutHeaderExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                  </span>
                )}
              </div>

              {hasChildren && isPopoutHeaderExpanded && (
                <List className="sidenav-popout-list" disablePadding>
                  {itemPopout.item.children?.map((child) => renderPopoutTreeItem(child, 0))}
                </List>
              )}
            </div>,
            document.body,
          )
        }
      </>
    );
  }

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      className="sidenav-drawer"
      PaperProps={{
        ref: sidebarPaperRef,
        className: 'sidenav-paper',
      }}
      ModalProps={{ keepMounted: true }}
    >
      {drawerContent}
    </Drawer>
  );
}
